import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Truck, MapPin, Clock, CheckCircle, AlertTriangle, Package, 
  Phone, Navigation, User, Building2, ArrowRight, RefreshCw,
  Circle, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Delivery progress steps
const DELIVERY_STEPS = [
  { id: 'assigned', label: 'Assigned', icon: User, description: 'Courier assigned to your delivery' },
  { id: 'picked_up', label: 'Picked Up', icon: Package, description: 'Cargo collected from origin' },
  { id: 'en_route', label: 'En Route', icon: Truck, description: 'Courier on the way' },
  { id: 'delivered', label: 'Delivered', icon: CheckCircle, description: 'Successfully delivered' }
];

function getStepStatus(currentStatus, stepId) {
  const statusOrder = ['assigned', 'accepted', 'picked_up', 'en_route', 'in_progress', 'delivered', 'completed'];
  const currentIndex = statusOrder.indexOf(currentStatus);
  const stepIndex = statusOrder.indexOf(stepId);
  
  // Map status to step
  const stepMapping = {
    'assigned': 0,
    'accepted': 0,
    'picked_up': 1,
    'en_route': 2,
    'in_progress': 2,
    'delivered': 3,
    'completed': 3
  };
  
  const currentStepIndex = stepMapping[currentStatus] ?? 0;
  const thisStepIndex = DELIVERY_STEPS.findIndex(s => s.id === stepId);
  
  if (thisStepIndex < currentStepIndex) return 'completed';
  if (thisStepIndex === currentStepIndex) return 'current';
  return 'pending';
}

export default function HospitalTrackingPage() {
  const { linkId } = useParams();
  const [tracking, setTracking] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const destinationMarkerRef = useRef(null);

  // Validate link and get initial tracking data
  const fetchTrackingData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API}/api/fleet/hospital-link/${linkId}`);
      if (res.status === 404) {
        setError('Tracking link not found');
        setLoading(false);
        return;
      }
      if (res.status === 410) {
        setError('Tracking link has expired');
        setLoading(false);
        return;
      }
      const data = await res.json();
      setTracking(data.tracking);
      setJobDetails(data.job_details || null);
      setLoading(false);
    } catch (err) {
      setError('Unable to load tracking information');
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTrackingData();
  }, [linkId]);

  // Initialize map
  useEffect(() => {
    if (loading || !mapRef.current || !tracking?.tracking_available || mapInstanceRef.current) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      const L = window.L;
      const { lat, lon } = tracking.current_location;
      
      const map = L.map(mapRef.current).setView([lat, lon], 13);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(map);

      // Courier marker with pulse animation
      const courierIcon = L.divIcon({
        className: 'courier-marker',
        html: `
          <div style="
            width: 52px; height: 52px;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border-radius: 50%;
            border: 4px solid white;
            box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
            display: flex; align-items: center; justify-content: center;
            animation: pulse 2s infinite;
          ">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
              <path d="M18 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5m1.5-9H17V12h4.46L19.5 9.5M6 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5M20 8l3 4v5h-2c0 1.66-1.34 3-3 3s-3-1.34-3-3H9c0 1.66-1.34 3-3 3s-3-1.34-3-3H1V6c0-1.11.89-2 2-2h14v4h3M3 6v9h.76c.55-.61 1.35-1 2.24-1s1.69.39 2.24 1H15V6H3z"/>
            </svg>
          </div>
        `,
        iconSize: [52, 52],
        iconAnchor: [26, 26]
      });

      const marker = L.marker([lat, lon], { icon: courierIcon }).addTo(map);
      markerRef.current = marker;

      // Add destination marker if available
      if (jobDetails?.delivery_lat && jobDetails?.delivery_lon) {
        const destIcon = L.divIcon({
          className: 'destination-marker',
          html: `
            <div style="
              width: 40px; height: 40px;
              background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
              border-radius: 50% 50% 50% 0;
              transform: rotate(-45deg);
              border: 3px solid white;
              box-shadow: 0 3px 10px rgba(239, 68, 68, 0.4);
              display: flex; align-items: center; justify-content: center;
            ">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style="transform: rotate(45deg);">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
          `,
          iconSize: [40, 40],
          iconAnchor: [20, 40]
        });
        
        const destMarker = L.marker([jobDetails.delivery_lat, jobDetails.delivery_lon], { icon: destIcon })
          .addTo(map)
          .bindPopup(`<b>Delivery Location</b><br/>${tracking.delivery_address || 'Your facility'}`);
        destinationMarkerRef.current = destMarker;

        // Fit bounds to show both markers
        const bounds = L.latLngBounds([
          [lat, lon],
          [jobDetails.delivery_lat, jobDetails.delivery_lon]
        ]);
        map.fitBounds(bounds, { padding: [50, 50] });
      }

      mapInstanceRef.current = map;
    };
    document.head.appendChild(script);
  }, [loading, tracking, jobDetails]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!tracking?.job_id || !tracking?.tracking_available) return;

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/fleet/ws/track/${tracking.job_id}`);

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'courier_move' || message.type === 'initial_state') {
          const data = message.data || message.courier;
          if (data) {
            setTracking(prev => ({
              ...prev,
              current_location: { lat: data.lat, lon: data.lon },
              eta_minutes: data.eta_minutes,
              courier_status: data.status,
              last_update: data.timestamp,
              distance_meters: data.distance_to_destination
            }));

            // Update marker position
            if (markerRef.current && window.L) {
              markerRef.current.setLatLng([data.lat, data.lon]);
              mapInstanceRef.current?.panTo([data.lat, data.lon]);
            }
          }
        }
      } catch (err) {
        console.error('WebSocket error:', err);
      }
    };

    // Keep alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [tracking?.job_id, tracking?.tracking_available]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500/30"></div>
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin"></div>
            <Truck className="absolute inset-0 m-auto w-8 h-8 text-emerald-400" />
          </div>
          <p className="text-lg">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-900/50 via-slate-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/80 backdrop-blur border-red-500/30">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Unable to Track</h2>
            <p className="text-red-200 mb-4">{error}</p>
            <p className="text-sm text-gray-400">
              Please contact your delivery coordinator for assistance.
            </p>
            <div className="mt-6 p-4 bg-slate-700/50 rounded-lg">
              <p className="text-gray-300 text-sm">Support Line</p>
              <a href="tel:+18009883892" className="text-emerald-400 font-medium flex items-center justify-center gap-2 mt-1">
                <Phone className="w-4 h-4" /> 800.988.3892
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tracking?.tracking_available) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-900/30 via-slate-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/80 backdrop-blur border-amber-500/30">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Package className="w-10 h-10 text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Awaiting Courier</h2>
            <p className="text-amber-200 mb-4">{tracking?.message || 'A courier has not been assigned yet.'}</p>
            <Badge className="bg-amber-500/20 text-amber-300 text-sm px-4 py-1">
              Status: {tracking?.status || 'Pending'}
            </Badge>
            <p className="text-sm text-gray-400 mt-4">
              Live tracking will begin once a courier is en route.
            </p>
            <Button 
              variant="outline" 
              className="mt-6 border-amber-500/50 text-amber-300 hover:bg-amber-500/20"
              onClick={() => fetchTrackingData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Check Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStep = getStepStatus(tracking.status, 'delivered');

  return (
    <div className="min-h-screen bg-slate-900" data-testid="hospital-tracking">
      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4); }
          50% { transform: scale(1.05); box-shadow: 0 4px 25px rgba(16, 185, 129, 0.6); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-in { animation: slideIn 0.5s ease-out forwards; }
      `}</style>

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-4 text-white shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur">
                <Truck className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Live Delivery Tracking</h1>
                <p className="text-sm text-emerald-100">Powered by AskIsaac</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => fetchTrackingData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* ETA Banner */}
      {tracking.eta_minutes && (
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 border-b border-slate-600">
          <div className="max-w-4xl mx-auto p-4">
            <div className="flex items-center justify-center gap-4">
              <Clock className="w-6 h-6 text-emerald-400" />
              <div className="text-center">
                <span className="text-4xl font-bold text-white">{tracking.eta_minutes}</span>
                <span className="text-lg text-gray-400 ml-2">minutes</span>
              </div>
              <span className="text-emerald-400 font-medium">Estimated Arrival</span>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex items-center justify-between relative">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-slate-600 mx-12 md:mx-16" />
            
            {DELIVERY_STEPS.map((step, index) => {
              const status = getStepStatus(tracking.status, step.id);
              const StepIcon = step.icon;
              
              return (
                <div key={step.id} className="relative z-10 flex flex-col items-center">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300
                    ${status === 'completed' ? 'bg-emerald-500 text-white' : ''}
                    ${status === 'current' ? 'bg-emerald-500 text-white ring-4 ring-emerald-500/30 animate-pulse' : ''}
                    ${status === 'pending' ? 'bg-slate-700 text-slate-400' : ''}
                  `}>
                    {status === 'completed' ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <StepIcon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`
                    text-xs mt-2 font-medium text-center
                    ${status === 'completed' || status === 'current' ? 'text-emerald-400' : 'text-slate-500'}
                  `}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Courier Status Bar */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${tracking.courier_status === 'moving' ? 'bg-green-500 animate-pulse' : 'bg-orange-500'}`} />
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-white font-medium">{tracking.courier_name}</span>
              </div>
              <Badge className={tracking.courier_status === 'moving' ? 'bg-green-500/20 text-green-300' : 'bg-orange-500/20 text-orange-300'}>
                {tracking.courier_status === 'moving' ? 'En Route' : 'Stopped'}
              </Badge>
            </div>
            
            {tracking.distance_meters && (
              <div className="flex items-center gap-2 text-gray-300">
                <Navigation className="w-4 h-4 text-blue-400" />
                <span>{(tracking.distance_meters / 1000).toFixed(1)} km away</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="h-[45vh] md:h-[50vh] relative">
        <div ref={mapRef} className="w-full h-full" />
        
        {/* Map Legend */}
        <div className="absolute bottom-4 left-4 bg-slate-800/90 backdrop-blur rounded-lg p-3 text-xs shadow-lg">
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-2 text-gray-300">
              <span className="w-3 h-3 rounded-full bg-emerald-500" /> Courier
            </span>
            <span className="flex items-center gap-2 text-gray-300">
              <span className="w-3 h-3 rounded-full bg-red-500" /> Destination
            </span>
          </div>
        </div>
      </div>

      {/* Delivery Details */}
      <div className="bg-slate-800 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Delivery Location */}
            <div className="bg-gradient-to-br from-slate-700/50 to-slate-700/30 rounded-xl p-4 border border-slate-600/50">
              <div className="flex items-center gap-2 text-emerald-400 mb-3">
                <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                  <Building2 className="w-4 h-4" />
                </div>
                <span className="font-medium">Delivery Location</span>
              </div>
              <p className="text-white font-medium">{tracking.delivery_address || 'Address not available'}</p>
              {jobDetails?.delivery_notes && (
                <p className="text-gray-400 text-sm mt-2">{jobDetails.delivery_notes}</p>
              )}
            </div>
            
            {/* Job Details */}
            <div className="bg-gradient-to-br from-slate-700/50 to-slate-700/30 rounded-xl p-4 border border-slate-600/50">
              <div className="flex items-center gap-2 text-blue-400 mb-3">
                <div className="p-1.5 bg-blue-500/20 rounded-lg">
                  <Package className="w-4 h-4" />
                </div>
                <span className="font-medium">Delivery Details</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <Badge className="bg-blue-500/20 text-blue-300">{tracking.status}</Badge>
                </div>
                {jobDetails?.cargo_type && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Cargo</span>
                    <span className="text-white">{jobDetails.cargo_type}</span>
                  </div>
                )}
                {jobDetails?.urgency && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Priority</span>
                    <Badge className={
                      jobDetails.urgency === 'critical' ? 'bg-red-500/20 text-red-300' :
                      jobDetails.urgency === 'stat' ? 'bg-orange-500/20 text-orange-300' :
                      'bg-blue-500/20 text-blue-300'
                    }>
                      {jobDetails.urgency.toUpperCase()}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-4 text-center">
            <p className="text-gray-500 text-sm">
              Last updated: {tracking.last_update ? new Date(tracking.last_update).toLocaleTimeString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-b from-slate-800 to-slate-900 p-6 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-400 text-sm mb-2">
            Questions about your delivery?
          </p>
          <a 
            href="tel:+18009883892" 
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
          >
            <Phone className="w-4 h-4" />
            800.988.3892
          </a>
          <div className="mt-4 pt-4 border-t border-slate-700">
            <p className="text-gray-600 text-xs">
              Real-time tracking provided by{' '}
              <a href="https://sterilemate.com" className="text-emerald-500 hover:text-emerald-400">
                AskIsaac.io
              </a>
              {' '}• On-Demand Healthcare Operations
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
